import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "@/lib/axios";

export interface DashboardData {
  user_id: number;
  user_email: string;
  user_name: string;
  role: string;
  dashboard_type: string;
  timestamp: string;
  database_status: string;
  [key: string]: any; // For role-specific data
}

// Helper function to make API calls using axiosInstance
const apiCall = async (endpoint: string) => {
  const response = await axiosInstance.get(endpoint);
  return response.data.data;
};

// Async thunks for fetching dashboard data
export const fetchRoleBasedDashboard = createAsyncThunk(
  'dashboard/fetchRoleBasedDashboard',
  async () => {
    return await apiCall('/dashboard/role-based');
  }
);

export const fetchMedicalReviewerDashboard = createAsyncThunk(
  'dashboard/fetchMedicalReviewerDashboard',
  async () => {
    return await apiCall('/dashboard/medical-reviewer');
  }
);

export const fetchCaseProcessorDashboard = createAsyncThunk(
  'dashboard/fetchCaseProcessorDashboard',
  async () => {
    return await apiCall('/dashboard/case-processor');
  }
);

export const fetchQualityReviewerDashboard = createAsyncThunk(
  'dashboard/fetchQualityReviewerDashboard',
  async () => {
    return await apiCall('/dashboard/quality-reviewer');
  }
);

export const fetchRegulatorySubmitterDashboard = createAsyncThunk(
  'dashboard/fetchRegulatorySubmitterDashboard',
  async () => {
    return await apiCall('/dashboard/regulatory-submitter');
  }
);

export const fetchFinaliseDashboard = createAsyncThunk(
  'dashboard/fetchFinaliseDashboard',
  async () => {
    return await apiCall('/dashboard/finaliser');
  }
);

export const fetchTriageDashboard = createAsyncThunk(
  'dashboard/fetchTriageDashboard',
  async () => {
    return await apiCall('/dashboard/triage');
  }
);

export const fetchDataEntryDashboard = createAsyncThunk(
  'dashboard/fetchDataEntryDashboard',
  async () => {
    return await apiCall('/dashboard/data-entry');
  }
);

export const fetchAdminDashboard = createAsyncThunk(
  'dashboard/fetchAdminDashboard',
  async () => {
    return await apiCall('/dashboard/admin');
  }
);

interface DashboardState {
  currentDashboard: DashboardData | null;
  loading: boolean;
  error: string | null;
  lastUpdated: string | null;
}

const initialState: DashboardState = {
  currentDashboard: null,
  loading: false,
  error: null,
  lastUpdated: null,
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    clearDashboard: (state) => {
      state.currentDashboard = null;
      state.error = null;
    },
    setDashboardData: (state, action: PayloadAction<DashboardData>) => {
      state.currentDashboard = action.payload;
      state.lastUpdated = new Date().toISOString();
    },
  },
  extraReducers: (builder) => {
    // Handle all dashboard fetch actions with the same pattern
    const handlePending = (state: DashboardState) => {
      state.loading = true;
      state.error = null;
    };

    const handleFulfilled = (state: DashboardState, action: PayloadAction<DashboardData>) => {
      state.loading = false;
      state.currentDashboard = action.payload;
      state.lastUpdated = new Date().toISOString();
    };

    const handleRejected = (state: DashboardState, action: any) => {
      state.loading = false;
      state.error = action.error.message || 'Failed to fetch dashboard data';
    };

    builder
      // Role-based dashboard
      .addCase(fetchRoleBasedDashboard.pending, handlePending)
      .addCase(fetchRoleBasedDashboard.fulfilled, handleFulfilled)
      .addCase(fetchRoleBasedDashboard.rejected, handleRejected)
      
      // Medical Reviewer dashboard
      .addCase(fetchMedicalReviewerDashboard.pending, handlePending)
      .addCase(fetchMedicalReviewerDashboard.fulfilled, handleFulfilled)
      .addCase(fetchMedicalReviewerDashboard.rejected, handleRejected)
      
      // Case Processor dashboard
      .addCase(fetchCaseProcessorDashboard.pending, handlePending)
      .addCase(fetchCaseProcessorDashboard.fulfilled, handleFulfilled)
      .addCase(fetchCaseProcessorDashboard.rejected, handleRejected)
      
      // Quality Reviewer dashboard
      .addCase(fetchQualityReviewerDashboard.pending, handlePending)
      .addCase(fetchQualityReviewerDashboard.fulfilled, handleFulfilled)
      .addCase(fetchQualityReviewerDashboard.rejected, handleRejected)
      
      // Regulatory Submitter dashboard
      .addCase(fetchRegulatorySubmitterDashboard.pending, handlePending)
      .addCase(fetchRegulatorySubmitterDashboard.fulfilled, handleFulfilled)
      .addCase(fetchRegulatorySubmitterDashboard.rejected, handleRejected)
      
      // Finalise dashboard
      .addCase(fetchFinaliseDashboard.pending, handlePending)
      .addCase(fetchFinaliseDashboard.fulfilled, handleFulfilled)
      .addCase(fetchFinaliseDashboard.rejected, handleRejected)
      
      // Triage dashboard
      .addCase(fetchTriageDashboard.pending, handlePending)
      .addCase(fetchTriageDashboard.fulfilled, handleFulfilled)
      .addCase(fetchTriageDashboard.rejected, handleRejected)
      
      // DataEntry dashboard
      .addCase(fetchDataEntryDashboard.pending, handlePending)
      .addCase(fetchDataEntryDashboard.fulfilled, handleFulfilled)
      .addCase(fetchDataEntryDashboard.rejected, handleRejected)
      
      // Admin dashboard
      .addCase(fetchAdminDashboard.pending, handlePending)
      .addCase(fetchAdminDashboard.fulfilled, handleFulfilled)
      .addCase(fetchAdminDashboard.rejected, handleRejected);
  },
});

export const { clearDashboard, setDashboardData } = dashboardSlice.actions;
export default dashboardSlice.reducer;

// Selectors
export const selectCurrentDashboard = (state: any) => state.dashboard.currentDashboard;
export const selectDashboardLoading = (state: any) => state.dashboard.loading;
export const selectDashboardError = (state: any) => state.dashboard.error;
export const selectDashboardLastUpdated = (state: any) => state.dashboard.lastUpdated;
